/**
 * URL Utilities
 * Handles conversion between framework IDs and output names in URLs
 */

/**
 * Converts a framework ID to URL format (dashes)
 * e.g., "non_fiction" -> "non-fiction"
 */
export function frameworkIdToUrl(frameworkId: string): string {
  return frameworkId.replace(/_/g, '-');
}

/**
 * Converts a URL framework ID back to internal format (underscores)
 * e.g., "non-fiction" -> "non_fiction"
 */
export function urlToFrameworkId(urlFrameworkId: string): string {
  return urlFrameworkId.replace(/-/g, '_');
}

/**
 * Strips the framework prefix from an output name
 * e.g., "non_fiction_welcome_letter" with frameworkId "non_fiction" -> "welcome_letter"
 */
export function stripFrameworkPrefix(outputName: string, frameworkId: string): string {
  const prefix = `${frameworkId}_`;
  if (outputName.startsWith(prefix)) {
    return outputName.substring(prefix.length);
  }
  return outputName;
}

/**
 * Adds framework prefix to a file name
 * e.g., "welcome_letter" with frameworkId "non_fiction" -> "non_fiction_welcome_letter"
 */
export function addFrameworkPrefix(fileName: string, frameworkId: string): string {
  return `${frameworkId}_${fileName}`;
}

/**
 * Builds a URL for an output file
 * @param projectId The project ID
 * @param frameworkId The framework ID (can have dashes or underscores)
 * @param outputName The output name (with framework prefix using underscores)
 * @returns The URL path
 */
export function buildOutputUrl(projectId: string, frameworkId: string, outputName: string): string {
  // Ensure frameworkId is in underscore format for prefix matching
  const frameworkIdWithUnderscores = frameworkId.replace(/-/g, '_');

  // Convert to URL format (dashes) for the URL path
  const urlFrameworkId = frameworkIdToUrl(frameworkIdWithUnderscores);

  // Strip the prefix using underscore format
  const fileName = stripFrameworkPrefix(outputName, frameworkIdWithUnderscores);

  return `/edit/${projectId}/${urlFrameworkId}/${encodeURIComponent(fileName)}`;
}

/**
 * Parses a URL to extract framework ID and construct the full output name
 * @param urlFrameworkId The framework ID from the URL (with dashes)
 * @param fileName The file name from the URL (without framework prefix)
 * @returns Object with internal frameworkId and full outputName
 */
export function parseOutputUrl(urlFrameworkId: string, fileName: string): { frameworkId: string; outputName: string } {
  const frameworkId = urlToFrameworkId(urlFrameworkId);
  const outputName = addFrameworkPrefix(fileName, frameworkId);
  return { frameworkId, outputName };
}
